// src/products/dataroom/components/Modals/BookmarkModal.js
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Bookmark as BookmarkIcon, Save, AlertCircle, Loader2 } from 'lucide-react'; // FIX: Imported Loader2
import dataroomService from '../../../../services/dataroomService';

const BookmarkModal = ({ isOpen, onClose, user, projectId, item, onBookmarkAdded, onMessage }) => {
  // Normalize item properties - handle both itemName/itemId and name/id formats
  const itemName = item?.itemName || item?.name || '';
  const itemId = item?.itemId || item?.id;

  const [formData, setFormData] = useState({
    name: itemName,
    notes: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen && item) {
      setFormData({
        name: itemName || '',
        notes: '',
      });
      setError(null);
    }
  }, [isOpen, item, itemName]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!(formData.name || '').trim()) {
      setError('Bookmark name is required.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (!user?.id || !projectId || !itemId) {
        throw new Error("Missing user or project context.");
      }

      const result = await dataroomService.createBookmark(
        user.id,
        projectId,
        itemId,
        (formData.name || '').trim(),
        (formData.notes || '').trim() || null
      );

      if (result.error) {
        throw result.error;
      }

      // Check if this was an update (bookmark already existed)
      const wasUpdate = result.data?.updated_at;
      onMessage(
        wasUpdate 
          ? `Bookmark "${formData.name}" updated successfully!` 
          : `Bookmarked "${formData.name}" successfully!`, 
        'success'
      );
      onBookmarkAdded(); // Notify parent to refresh/update state
      onClose();
    } catch (err) {
      console.error('Error creating bookmark:', err);
      setError(err.message.includes('unique_user_item_per_project') 
        ? 'You have already bookmarked this item.' 
        : `Failed to save bookmark: ${err.message}`
      );
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !item) return null;

  return createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold flex items-center text-gray-900">
            <BookmarkIcon className="h-5 w-5 mr-3 text-green-600" />
            Create New Bookmark
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors" disabled={loading}>
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSave} className="p-6 space-y-4">
          
          {error && (
            <div className="flex items-start p-3 bg-red-50 border border-red-200 rounded-lg">
              <AlertCircle className="h-5 w-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
              <span className="text-red-700 text-sm">{error}</span>
            </div>
          )}

          {/* Item Info */}
          <div className="bg-gray-50 p-3 rounded-lg text-sm">
            <p className="font-medium text-gray-900">Item: {itemName || 'Unknown'}</p>
            <p className="text-xs text-gray-600">ID: {itemId || 'N/A'}</p>
          </div>

          {/* Name Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Bookmark Name *
            </label>
            <input
              type="text"
              name="name"
              value={formData.name || ''}
              onChange={handleInputChange}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="e.g., Q4 Financials - Needs Review"
              required
              maxLength={100}
            />
          </div>

          {/* Notes Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notes (Optional)
            </label>
            <textarea
              name="notes"
              value={formData.notes || ''}
              onChange={handleInputChange}
              rows={3}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="e.g., Missing FY2025 detailed data? Check with finance team."
            />
            <p className="text-xs text-gray-500 mt-1">
              Use this to add reminders or context to your bookmark.
            </p>
          </div>

          {/* Footer */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 bg-white">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !(formData.name || '').trim()}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center transition-colors focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save Bookmark
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default BookmarkModal;